import {
  CheckBadgeIcon,
  PauseIcon as PauseMiniIcon,
  PlayIcon as PlayMiniIcon,
} from "@heroicons/react/20/solid";
import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import useSWRMutation from "swr/mutation";

import { mutateRelatedPokeList } from "~/hook/domain/useRelatedPokeList.ts";
import { DELETED_USER } from "~/service/const.ts";
import { Poke, User } from "~/service/dataType.ts";
import { getReadableDateOffset } from "~/service/util.ts";
import { AppLink } from "~/ui/base/AppLink.tsx";
import { type Line } from "~/ui/base/Canvas.tsx";
import { useUser } from "~/ui/provider/Auth.tsx";
import {
  useOverlaySheets,
  useStackedLayer,
} from "~/ui/provider/StackedLayerProvider.tsx";

interface PocketListItemProps {
  animation: null | {
    delayTimes: number;
  };
  date: string;
  id: number;
  isVerifiedUser: boolean;
  payload: Poke["payload"];
  targetUser: User;
  type: "poke" | "poked";
}

// eslint-disable-next-line @lee-donghyun/lingui/no-unlocalized-strings
const EMOJIS = ["❤️", "👍", "😂", "🔥"];

const cx = {
  watchButton:
    "inline-flex items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800 px-1 align-middle font-medium",
};

const NormalPokeBody = ({ targetUserName }: { targetUserName: string }) => (
  <p className="text-sm text-zinc-800 dark:text-zinc-200">
    <Trans>
      회원님이 <span className="font-semibold">{targetUserName}</span>
      님을 콕 찔렀습니다
    </Trans>
  </p>
);

const NormalPokedBody = ({ targetUserName }: { targetUserName: string }) => (
  <p className="text-sm text-zinc-800 dark:text-zinc-200">
    <Trans>
      <span className="font-semibold">{targetUserName}</span>님이 회원님을 콕
      찔렀습니다
    </Trans>
  </p>
);

const EmojiPokeBody = ({
  message,
  targetUserName,
}: {
  message: string;
  targetUserName: string;
}) => (
  <p className="text-sm text-zinc-800 dark:text-zinc-200">
    <Trans>
      회원님이 <span className="font-semibold">{targetUserName}</span>
      님에게 메세지를 보냈습니다: {message}
    </Trans>
  </p>
);

const EmojiPokedBody = ({
  message,
  targetUserName,
}: {
  message: string;
  targetUserName: string;
}) => (
  <p className="text-sm text-zinc-800 dark:text-zinc-200">
    <Trans>
      <span className="font-semibold">{targetUserName}</span>님이 회원님에게
      메세지를 보냈습니다: {message}
    </Trans>
  </p>
);

const DrawingPokeBody = ({
  lines,
  targetUserName,
}: {
  lines: Line[];
  targetUserName: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        회원님이 <span className="font-semibold">{targetUserName}</span>
        님에게 그림을 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="보낸 그림 보기"
        onClick={() => {
          overlay(sheets.showDrawing, {
            lines,
            title: t`${targetUserName}님에게 보낸 그림`,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const DrawingPokedBody = ({
  lines,
  targetUserName,
}: {
  lines: Line[];
  targetUserName: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        <span className="font-semibold">{targetUserName}</span>님이 회원님에게
        그림을 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="받은 그림 보기"
        onClick={() => {
          overlay(sheets.showDrawing, {
            lines,
            title: t`${targetUserName}님이 보낸 그림`,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const GeolocationPokeBody = ({
  position,
  targetUserName,
}: {
  position: { latitude: number; longitude: number };
  targetUserName: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        회원님이 <span className="font-semibold">{targetUserName}</span>
        님에게 위치를 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="보낸 위치 보기"
        onClick={() => {
          overlay(sheets.showGeolocation, {
            position,
            title: t`${targetUserName}님에게 보낸 위치`,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const GeolocationPokedBody = ({
  position,
  targetUserName,
}: {
  position: { latitude: number; longitude: number };
  targetUserName: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        <span className="font-semibold">{targetUserName}</span>님이 회원님에게
        위치를 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="받은 위치 보기"
        onClick={() => {
          overlay(sheets.showGeolocation, {
            position,
            title: t`${targetUserName}님이 보낸 위치`,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const PicturePokeBody = ({
  effect,
  targetUserName,
  videoUrl,
}: {
  effect: "bounce" | "still";
  targetUserName: string;
  videoUrl: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        회원님이 <span className="font-semibold">{targetUserName}</span>
        님에게 사진을 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="보낸 사진 보기"
        onClick={() => {
          overlay(sheets.showPicture, {
            effect,
            title: t`${targetUserName}님에게 보낸 사진`,
            videoUrl,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const PicturePokedBody = ({
  effect,
  targetUserName,
  videoUrl,
}: {
  effect: "bounce" | "still";
  targetUserName: string;
  videoUrl: string;
}) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        <span className="font-semibold">{targetUserName}</span>님이 회원님에게
        사진을 보냈습니다:
      </Trans>{" "}
      <button
        className={cx.watchButton}
        data-testid="받은 사진 보기"
        onClick={() => {
          overlay(sheets.showPicture, {
            effect,
            title: t`${targetUserName}님이 보낸 사진`,
            videoUrl,
          });
        }}
        type="button"
      >
        <Trans>보기</Trans>
      </button>
    </p>
  );
};

const AudioPokeBody = ({
  audioUrl,
  duration,
  targetUserName,
}: {
  audioUrl: string;
  duration?: number;
  targetUserName: string;
}) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setPlaying(false);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      void audioRef.current.play().then(() => {
        setPlaying(true);
      });
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        회원님이 <span className="font-semibold">{targetUserName}</span>
        님에게 소리 콕!을 보냈습니다:
      </Trans>{" "}
      <button className={cx.watchButton} onClick={togglePlay} type="button">
        {playing ? (
          <PauseMiniIcon className="mr-0.5 inline size-4 align-middle text-zinc-600 dark:text-zinc-400" />
        ) : (
          <PlayMiniIcon className="mr-0.5 inline size-4 align-middle text-zinc-600 dark:text-zinc-400" />
        )}
        <span className="align-middle">
          {duration ? `${duration.toFixed(1)}s` : <Trans>듣기</Trans>}
        </span>
      </button>
    </p>
  );
};

const AudioPokedBody = ({
  audioUrl,
  duration,
  targetUserName,
}: {
  audioUrl: string;
  duration?: number;
  targetUserName: string;
}) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setPlaying(false);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      void audioRef.current.play().then(() => {
        setPlaying(true);
      });
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return (
    <p className="text-sm text-zinc-800 dark:text-zinc-200">
      <Trans>
        <span className="font-semibold">{targetUserName}</span>님이 회원님에게
        소리 콕!을 보냈습니다:
      </Trans>{" "}
      <button className={cx.watchButton} onClick={togglePlay} type="button">
        {playing ? (
          <PauseMiniIcon className="mr-0.5 inline size-4 align-middle text-zinc-600 dark:text-zinc-400" />
        ) : (
          <PlayMiniIcon className="mr-0.5 inline size-4 align-middle text-zinc-600 dark:text-zinc-400" />
        )}
        <span className="align-middle">
          {duration ? `${duration.toFixed(1)}s` : <Trans>듣기</Trans>}
        </span>
      </button>
    </p>
  );
};

export const PokeListItem = ({
  animation,
  date,
  id,
  isVerifiedUser,
  payload,
  targetUser,
  type,
}: PocketListItemProps) => {
  const overlay = useStackedLayer();
  const sheets = useOverlaySheets();
  const { t } = useLingui();
  const { client } = useUser();
  const targetUserName = targetUser.name;

  const { isMutating: isReacting, trigger: triggerReact } = useSWRMutation(
    `mate/poke/${String(id)}/react`,
    (url, { arg }: { arg: { reaction: string } }) =>
      client.post(url, { json: arg }).json(),
    {
      onError: (err) => {
        console.error("Failed to react to poke:", err);
      },
      onSuccess: () => {
        void mutateRelatedPokeList();
      },
    },
  );

  const handleReact = (reaction: string) => {
    const nextReaction = payload.reaction === reaction ? "" : reaction;
    void triggerReact({ reaction: nextReaction });
  };

  return (
    <li
      className="block"
      {...(animation && {
        className: "animate-from-bottom opacity-0 block",
        style: {
          animationDelay: `${String(animation.delayTimes * 50)}ms`,
          transform: "translateY(60px)",
        },
      })}
    >
      <div className="flex">
        <img
          alt={t`${targetUserName} 프로필 이미지`}
          className="mt-1 h-8 w-8 min-w-[2rem] rounded-full bg-zinc-200 object-cover dark:bg-zinc-800"
          src={targetUser.profileImageUrl ?? "/asset/default_user_profile.png"}
        />
        <div className="ml-4 min-w-0 flex-1 wrap-anywhere">
          <p className="flex items-start gap-2">
            <AppLink
              className="flex min-w-0 items-center font-medium"
              pathname={`/user/${targetUser.email}`}
              role="link"
            >
              <span className="truncate">@{targetUser.email}</span>
              {isVerifiedUser && (
                <span className="ml-0.5 shrink-0 text-blue-500">
                  <CheckBadgeIcon className="size-[1.125rem]" />
                </span>
              )}
            </AppLink>
            <span className="mt-1 ml-auto shrink-0 text-xs font-normal whitespace-nowrap text-zinc-400">
              {t(getReadableDateOffset(date))}
            </span>
          </p>
          {payload.type === "normal" && type === "poke" && (
            <NormalPokeBody targetUserName={targetUser.name} />
          )}
          {payload.type === "normal" && type === "poked" && (
            <NormalPokedBody targetUserName={targetUser.name} />
          )}
          {payload.type === "emoji" && type === "poke" && (
            <EmojiPokeBody
              message={payload.message}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "emoji" && type === "poked" && (
            <EmojiPokedBody
              message={payload.message}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "drawing" && type === "poke" && (
            <DrawingPokeBody
              lines={payload.lines}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "drawing" && type === "poked" && (
            <DrawingPokedBody
              lines={payload.lines}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "geolocation" && type === "poke" && (
            <GeolocationPokeBody
              position={payload.position}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "geolocation" && type === "poked" && (
            <GeolocationPokedBody
              position={payload.position}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "picture" && type === "poke" && (
            <PicturePokeBody
              effect={payload.effect}
              targetUserName={targetUser.name}
              videoUrl={payload.videoUrl}
            />
          )}
          {payload.type === "picture" && type === "poked" && (
            <PicturePokedBody
              effect={payload.effect}
              targetUserName={targetUser.name}
              videoUrl={payload.videoUrl}
            />
          )}
          {payload.type === "audio" && type === "poke" && (
            <AudioPokeBody
              audioUrl={(payload as { audioUrl: string }).audioUrl}
              duration={(payload as { duration?: number }).duration}
              targetUserName={targetUser.name}
            />
          )}
          {payload.type === "audio" && type === "poked" && (
            <AudioPokedBody
              audioUrl={(payload as { audioUrl: string }).audioUrl}
              duration={(payload as { duration?: number }).duration}
              targetUserName={targetUser.name}
            />
          )}
          {payload.reaction && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs dark:border-zinc-700 dark:bg-zinc-800">
                <span>{payload.reaction}</span>
                <span className="text-[10px] text-zinc-500">
                  {type === "poked" ? t`나의 반응` : t`상대방의 반응`}
                </span>
              </span>
            </div>
          )}
          {type === "poked" && targetUser !== DELETED_USER && (
            <div className="mt-1.5 flex gap-2">
              <button
                className="min-w-0 flex-1 truncate rounded-xl bg-zinc-100 p-1 text-sm font-medium text-zinc-900 active:opacity-60 disabled:opacity-60 dark:bg-zinc-800 dark:text-zinc-100"
                data-testid="나도 콕 찌르기"
                onClick={() => {
                  overlay(sheets.poke, {
                    targetUserEmail: targetUser.email,
                  });
                }}
              >
                <Trans>나도 콕! 찌르기 👉</Trans>
              </button>
              <div className="flex shrink-0 gap-1">
                {EMOJIS.map((emoji) => (
                  <button
                    className={`flex h-7 w-7 items-center justify-center rounded-xl text-sm duration-100 active:scale-125 ${
                      payload.reaction === emoji
                        ? "bg-zinc-300 dark:bg-zinc-600"
                        : "bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                    } disabled:opacity-50`}
                    disabled={isReacting}
                    key={emoji}
                    onClick={() => {
                      handleReact(emoji);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};
